// API layer for guided-financial-planning feature

import { WaterlilyClient, createQueryKeys } from '@client/api/waterlily';

// Query keys for each guided planning domain
export const guidedPlanningSessionKeys = createQueryKeys('guidedPlanningSession');
export const guidedPlanningFundingSourceKeys = createQueryKeys('guidedPlanningFundingSource');
export const guidedPlanningQuoteKeys = createQueryKeys('guidedPlanningQuote');
export const underwritingResultsKeys = createQueryKeys('underwritingResults');

export const guidedPlanningApi = {
  // =====================================================
  // Session API
  // =====================================================

  /**
   * Get all sessions for a client, ordered by most recent first.
   * The client decides which session to render.
   */
  getSessions: (clientId: string) =>
    WaterlilyClient.get<GuidedPlanningSessionResponse[]>(
      `/client/${clientId}/guided-planning-session`,
    ),

  /**
   * Create a new session or update an existing one.
   * - If sessionId is provided in body, updates that session.
   * - If sessionId is omitted, creates a new session.
   */
  upsertSession: (clientId: string, data: PutGuidedPlanningSessionBody) =>
    WaterlilyClient.put<GuidedPlanningSessionResponse>(
      `/client/${clientId}/guided-planning-session`,
      data,
    ),

  // =====================================================
  // Quotes API
  // =====================================================

  /**
   * Fetch quotes for the guided planning flow.
   * The server fetches selectedPolicyCategories and preferences from the planning session.
   * Returns quotes with metadata about why no quotes may be available.
   */
  getQuotes: (clientId: string, sessionId: string) =>
    WaterlilyClient.get<GuidedPlanningQuotesResponse>(
      `/client/${clientId}/guided-planning-quotes`,
      { sessionId },
    ),

  // =====================================================
  // Funding Sources API
  // =====================================================

  upsertSelfFunding: (params: {
    clientId: string;
    fundingSourceId: string | null;
    fundingSourceData: SelfFundingSource;
  }) =>
    WaterlilyClient.put<PutMultipleFundingSourceResponse>(
      `/client/${params.clientId}/multiple-funding-source`,
      {
        clientId: params.clientId,
        fundingSourceId: params.fundingSourceId,
        fundingSourceType: 'selfFunding',
        fundingSourceData: params.fundingSourceData,
      },
    ),

  /**
   * Batch update existingAssetsValue for multiple self-funding sources.
   * Used to apply funding deductions when finalizing a guided planning session.
   */
  batchUpdateSelfFundingAssets: (params: {
    clientId: string;
    updates: Array<{ fundingSourceId: string; existingAssetsValue: number }>;
  }) =>
    WaterlilyClient.put<Client>(
      `/client/${params.clientId}/batch-self-funding-existing-assets-value`,
      { clientId: params.clientId, updates: params.updates },
    ),
};

// =====================================================
// Underwriting Results API
// =====================================================

export const underwritingApi = {
  /**
   * Fetch cached underwriting eligibility results for all products.
   * Returns a map of productName to eligibility status.
   */
  getResults: (clientId: string) =>
    WaterlilyClient.get<UnderwritingResultsResponse>(
      `/client/${clientId}/underwriting-results`,
    ),
};
